<template>
  <div class="user-feedback bg-gradient">
    <el-card class="mb-6 shadow-lg rounded-xl border-0 bg-white/80">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="关键词" class="search-item">
          <el-input
            v-model="searchForm.keyword"
            placeholder="输入用户名/单位/内容关键词"
            clearable
            @clear="handleSearch"
            size="large"
            class="rounded-full shadow-sm search-input"
          >
            <template #append>
              <el-button type="primary" @click="handleSearch" size="large" class="rounded-full">
                <el-icon><Search /></el-icon>
                搜索
              </el-button>
            </template>
          </el-input>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="table-card shadow-xl rounded-xl border-0 bg-white/90">
      <template #header>
        <div class="card-header flex items-center justify-between">
          <span class="text-xl font-bold text-gray-700">用户反馈列表</span>
        </div>
      </template>

      <div class="table-container">
        <el-table
          v-loading="loading"
          :data="feedbackList"
          style="width: 100%"
          border
          stripe
          size="large"
          header-cell-class-name="header-cell"
        >
          <el-table-column prop="name" label="用户名" min-width="120" align="center"/>
          <el-table-column prop="profession" label="单位类型" min-width="120" align="center"/>
          <el-table-column prop="institution_name" label="单位名称" min-width="140" align="center" show-overflow-tooltip />
          <el-table-column prop="organization" label="个人学历" min-width="120" align="center"/>
          <el-table-column prop="content" label="反馈内容" min-width="220" show-overflow-tooltip />
          <el-table-column prop="reply" label="回复内容" min-width="200" show-overflow-tooltip>
            <template #default="{ row }">
              <span v-if="row.reply" class="reply-text">{{ row.reply }}</span>
              <span v-else class="no-reply">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="created_at" label="提交时间" min-width="160" align="center">
            <template #default="{ row }">
              {{ formatDate(row.created_at) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" min-width="120" align="center" fixed="right">
            <template #default="{ row }">
              <el-button type="primary" size="small" @click="handleReply(row)">
                {{ row.reply ? '查看回复' : '回复' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-container flex justify-end mt-4">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 回复对话框 -->
    <el-dialog
      v-model="replyDialogVisible"
      title="反馈回复"
      width="600px"
      class="reply-dialog"
    >
      <div v-if="currentFeedback" class="feedback-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="用户名">{{ currentFeedback.name }}</el-descriptions-item>
          <el-descriptions-item label="邮箱">{{ currentFeedback.email }}</el-descriptions-item>
          <el-descriptions-item label="单位类型">{{ currentFeedback.profession }}</el-descriptions-item>
          <el-descriptions-item label="单位名称">{{ currentFeedback.institution_name }}</el-descriptions-item>
          <el-descriptions-item label="个人学历">{{ currentFeedback.organization }}</el-descriptions-item>
          <el-descriptions-item label="反馈来源">
            <el-tag :type="currentFeedback.source === 'website' ? 'success' : 'info'" size="small">
              {{ currentFeedback.source === 'website' ? '网站端' : '程序端' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="反馈内容" :span="2">
            <div class="feedback-content">{{ currentFeedback.content }}</div>
          </el-descriptions-item>
          <el-descriptions-item label="当前状态" :span="2">
            <el-tag :type="getStatusType(currentFeedback.status)" size="small">
              {{ getStatusText(currentFeedback.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item v-if="currentFeedback.reply" label="已有回复" :span="2">
            <div class="existing-reply">{{ currentFeedback.reply }}</div>
          </el-descriptions-item>
        </el-descriptions>

        <div class="reply-form mt-4">
          <el-form :model="replyForm" label-width="80px">
            <el-form-item label="处理状态">
              <el-select v-model="replyForm.status" placeholder="选择处理状态">
                <el-option label="待处理" value="pending" />
                <el-option label="已处理" value="resolved" />
              </el-select>
            </el-form-item>
            <el-form-item label="回复内容">
              <el-input
                v-model="replyForm.reply"
                type="textarea"
                :rows="4"
                placeholder="请输入回复内容"
              />
            </el-form-item>
          </el-form>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="replyDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmitReply" :loading="submitting">
            提交回复
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import axios from 'axios'

// 搜索表单
const searchForm = reactive({
  keyword: ''
})

// 表格数据
const loading = ref(false)
const feedbackList = ref([])

// 分页
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)

// 回复对话框
const replyDialogVisible = ref(false)
const currentFeedback = ref(null)
const submitting = ref(false)
const replyForm = reactive({
  status: '',
  reply: ''
})

// 获取反馈列表
const fetchFeedbackList = async () => {
  loading.value = true
  try {
    const response = await axios.get('/api/admin/users/feedback', {
      params: {
        page: currentPage.value,
        limit: pageSize.value,
        keyword: searchForm.keyword || undefined
      }
    })
    feedbackList.value = response.data.data
    total.value = response.data.total
  } catch (error) {
    ElMessage.error('获取反馈列表失败：' + error.message)
  } finally {
    loading.value = false
  }
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// 搜索
const handleSearch = () => {
  currentPage.value = 1
  fetchFeedbackList()
}

// 分页
const handleSizeChange = (val) => {
  pageSize.value = val
  fetchFeedbackList()
}

const handleCurrentChange = (val) => {
  currentPage.value = val
  fetchFeedbackList()
}

// 获取状态类型
const getStatusType = (status) => {
  const statusMap = {
    'pending': 'warning',
    'resolved': 'success'
  }
  return statusMap[status] || 'info'
}

// 获取状态文本
const getStatusText = (status) => {
  const statusMap = {
    'pending': '待处理',
    'resolved': '已处理'
  }
  return statusMap[status] || status
}

// 处理回复
const handleReply = (row) => {
  currentFeedback.value = row
  replyForm.status = row.status || 'pending'
  replyForm.reply = row.reply || ''
  replyDialogVisible.value = true
}

// 提交回复
const handleSubmitReply = async () => {
  if (!replyForm.reply.trim()) {
    ElMessage.warning('请输入回复内容')
    return
  }

  submitting.value = true
  try {
    await axios.put(`/api/admin/users/feedback/${currentFeedback.value.id}`, {
      status: replyForm.status,
      reply: replyForm.reply,
      source: currentFeedback.value.source
    })
    
    ElMessage.success('回复提交成功')
    replyDialogVisible.value = false
    fetchFeedbackList()
  } catch (error) {
    ElMessage.error('回复提交失败：' + error.message)
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  fetchFeedbackList()
})
</script>

<style scoped>
.user-feedback {
  padding: 0.75rem;
}

@media (min-width: 768px) {
  .user-feedback {
    padding: 1rem;
  }
}

.search-form {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-start;
}

.search-item {
  margin-bottom: 0;
  min-width: 200px;
}

.search-item :deep(.el-select) {
  width: 100%;
}

.search-item :deep(.el-input) {
  width: 100%;
}

.card-header {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
}

@media (min-width: 768px) {
  .card-header {
    flex-direction: row;
    align-items: center;
  }
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.table-container {
  overflow-x: auto;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.pagination-container {
  margin-top: 0.75rem;
  display: flex;
  justify-content: flex-end;
}

.reply-form {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

.form-title {
  margin-bottom: 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
}

:deep(.el-card__header) {
  padding: 0.75rem;
}

:deep(.el-table) {
  margin-top: 0.75rem;
  font-size: 0.8125rem;
}

:deep(.el-pagination) {
  margin-top: 0.75rem;
  font-size: 0.8125rem;
}

:deep(.el-form-item__label) {
  font-size: 0.8125rem;
  line-height: 28px;
  width: 80px !important;
  text-align: right;
}

:deep(.el-input__inner) {
  font-size: 0.8125rem;
  height: 28px;
  line-height: 28px;
}

:deep(.el-button) {
  font-size: 0.8125rem;
  padding: 5px 10px;
}

:deep(.el-dialog__title) {
  font-size: 0.9375rem;
}

:deep(.el-dialog__body) {
  font-size: 0.8125rem;
}

:deep(.el-tag) {
  font-size: 0.75rem;
  padding: 0 8px;
  height: 22px;
  line-height: 20px;
}

/* 响应式布局调整 */
@media screen and (max-width: 768px) {
  .search-form {
    flex-direction: column;
  }

  .search-item {
    width: 100%;
  }

  .el-form-item {
    width: 100%;
    margin-bottom: 0.75rem;
  }

  .el-input {
    width: 100%;
  }

  .el-select {
    width: 100%;
  }

  .action-buttons {
    flex-direction: column;
  }

  .action-buttons .el-button {
    width: 100%;
  }
}

/* 表格在小屏幕上的优化 */
@media screen and (max-width: 640px) {
  :deep(.el-table) {
    font-size: 0.75rem;
  }

  :deep(.el-table .cell) {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
  }

  :deep(.el-table__body-wrapper) {
    overflow-x: auto;
  }

  :deep(.el-table__header-wrapper) {
    overflow-x: auto;
  }

  :deep(.el-pagination) {
    flex-wrap: wrap;
    justify-content: center;
  }
}

@media screen and (max-width: 480px) {
  :deep(.el-table) {
    font-size: 0.7rem;
  }

  :deep(.el-table .cell) {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }
}

/* 对话框响应式调整 */
@media screen and (max-width: 480px) {
  .feedback-dialog {
    width: 90%;
    max-width: 320px;
  }
}

/* 回复对话框样式 */
.reply-dialog {
  border-radius: 12px;
}

.feedback-detail {
  margin-bottom: 16px;
}

.feedback-content {
  max-height: 100px;
  overflow-y: auto;
  padding: 8px;
  background-color: #f5f7fa;
  border-radius: 4px;
  line-height: 1.5;
}

.existing-reply {
  max-height: 80px;
  overflow-y: auto;
  padding: 8px;
  background-color: #e8f5e8;
  border-radius: 4px;
  line-height: 1.5;
  border-left: 3px solid #67c23a;
}

.reply-form {
  border-top: 1px solid #ebeef5;
  padding-top: 16px;
}

:deep(.el-descriptions__label) {
  font-weight: 600;
  color: #606266;
}

:deep(.el-descriptions__content) {
  color: #303133;
}

/* 回复内容样式 */
.reply-text {
  color: #67c23a;
  font-weight: 500;
}

.no-reply {
  color: #c0c4cc;
  font-style: italic;
}
</style>
